import React from "react";
import { useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";
import styled from "styled-components";
import { Button } from "@fluentui/react-components";
import { ArrowRight20Regular } from "@fluentui/react-icons";
import PrefillSection from "./sections/PrefillSection";
// import ProposalDataSection from "./sections/ProposalDataSection";
import ClientDataSection from "./sections/ClientDataSection";
import VehicleDataSection from "./sections/VehicleDataSection";
import { ValidationError } from "utils/validation/calculationValidation";
import { ValidationSummary } from "components/ValidationSummary/ValidationSummary";

const TabWrapper = styled.div<{ $isPrefilling?: boolean }>`
  display: flex;
  flex-direction: column;
  height: 100%;

  ${(props) =>
    props.$isPrefilling &&
    `
    pointer-events: none;
    opacity: 0.6;
    user-select: none;
    
    /* Re-enable pointer events for PrefillSection only */
    > div:first-child {
      pointer-events: auto;
      opacity: 1;
    }
  `}
`;

const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 17px 0 6px 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FooterPanel = styled.div`
  flex-shrink: 0;
  background: ${({ theme }) => theme.semanticColors.bodyBackground};
  border-top: 1px solid ${({ theme }) => theme.semanticColors.bodyFrameDivider};
  padding: 16px 24px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

interface GeneralInfoTabProps {
  validationErrors: ValidationError[];
  getError: (fieldPath: string) => string | undefined;
  clearErrors: () => void;
  scrollToFirstError: () => void;
  isPrefilling?: boolean;
  isNewProposal?: boolean;
  onContinueToProposal?: () => void;
  offerId?: string | null;
}

const GeneralInfoTab: React.FC<GeneralInfoTabProps> = ({
  validationErrors,
  getError,
  clearErrors,
  scrollToFirstError,
  isPrefilling = false,
  isNewProposal = false,
  onContinueToProposal,
  offerId,
}) => {
  const { t } = useTranslation();
  const { watch } = useFormContext();

  const insuranceProduct = watch("proposalData.insuranceProduct");
  const isCasco = insuranceProduct?.toLowerCase() === "casco";

  // Filter validation errors for General Info tab fields
  const generalInfoErrors = validationErrors.filter((error) => {
    const field = error.field.toLowerCase();
    return (
      field.startsWith("client.") ||
      field.startsWith("vehicle.") ||
      field.startsWith("policy.")
    );
  });

  return (
    <TabWrapper $isPrefilling={isPrefilling}>
      <ScrollableContent>
        {generalInfoErrors.length > 0 && (
          <ValidationSummary
            errors={generalInfoErrors}
            title="Please correct the following errors:"
          />
        )}
        <PrefillSection getError={getError} offerId={offerId} />
        {/* ProposalDataSection commented out - replaced with compact header display in tab area */}
        {/* <ProposalDataSection /> */}
        <VehicleDataSection
          isCasco={isCasco}
          getError={getError}
          isPrefilling={isPrefilling}
          offerId={offerId}
        />
        <ClientDataSection
          isCasco={isCasco}
          getError={getError}
          isPrefilling={isPrefilling}
          offerId={offerId}
        />
      </ScrollableContent>

      {isNewProposal && onContinueToProposal && (
        <FooterPanel>
          <Button
            data-help="offer-general-continue-button"
            appearance="subtle"
            icon={<ArrowRight20Regular />}
            iconPosition="after"
            onClick={onContinueToProposal}
          >
            {t("qtg.proposal.continueToProposal", "Continue to Proposal")}
          </Button>
        </FooterPanel>
      )}
    </TabWrapper>
  );
};

export default GeneralInfoTab;
